import { cur } from '@/lib/currency';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { DrawerSourceSelect } from '@/components/DrawerSourceSelect';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DollarSign, ShoppingCart, Receipt, CreditCard, Banknote, CheckCircle, Users, Truck, Package, Plus, Loader2, Calendar, UserPlus, HelpCircle, ScanLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useBranchStore, useAuthStore } from '@/stores';
import BranchTaxBadge from '@/components/BranchTaxBadge';
import { PosFirstSaleTour } from '@/components/pos/PosFirstSaleTour';

// Platform colors for visual distinction
const PLATFORM_COLORS = {
  'HungerStation': 'bg-error/10 border-error/30 focus:border-red-400',
  'Hunger': 'bg-orange-50 border-orange-200 focus:border-orange-400',
  'Jahez': 'bg-success/10 border-success/30 focus:border-green-400',
  'ToYou': 'bg-info/10 border-info/30 focus:border-blue-400',
  'Keta': 'bg-purple-50 border-purple-200 focus:border-purple-400',
  'Ninja': 'bg-warning/10 border-warning/30 focus:border-yellow-400',
  'Careem Food': 'bg-teal-50 border-teal-200 focus:border-teal-400',
  'Talabat': 'bg-pink-50 border-pink-200 focus:border-pink-400',
  'Marsool': 'bg-indigo-50 border-indigo-200 focus:border-indigo-400',
};

export default function POSPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Zustand stores
  const { branches, fetchBranches } = useBranchStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  
  const [customers, setCustomers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  // Show our new 4-step "First Sale" narrative tour. Complements the
  // existing element-spotlight PageTour without replacing it — users
  // get both: a quick tour-tooltip for veterans, and a richer
  // walkthrough modal for first-timers.
  const [showFirstSaleTour, setShowFirstSaleTour] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [branch, setBranch] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [entryType, setEntryType] = useState('sale');
  const [saleMode, setSaleMode] = useState('regular'); // 'regular' or 'online'
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastEntries, setLastEntries] = useState([]);
  const [todayStats, setTodayStats] = useState({ sales: 0, expenses: 0, count: 0, onlineSales: 0 });
  const [paymentMode, setPaymentMode] = useState('cash');
  
  // Date selection
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Multi Supplier Payments - array of {supplier_id, amount, payment_mode}
  const [supplierPayments, setSupplierPayments] = useState([{ supplier_id: '', amount: '', payment_mode: 'cash' }]);
  const [submittingPayments, setSubmittingPayments] = useState(false);

  // Multi Expenses - array of {category, amount, payment_mode, supplier_id, description}
  const [expenses, setExpenses] = useState([{ category: '', amount: '', payment_mode: 'cash', supplier_id: '', description: '' }]);
  const [expenseDrawer, setExpenseDrawer] = useState('');
  const [submittingExpenses, setSubmittingExpenses] = useState(false);

  // Multi Supplier Bills - array of {supplier_id, amount, payment_mode, description}
  const [supplierBills, setSupplierBills] = useState([{ supplier_id: '', amount: '', payment_mode: 'credit', description: '' }]);
  const [submittingBills, setSubmittingBills] = useState(false);

  // Regular sale amounts
  const [cashAmount, setCashAmount] = useState('');
  const [bankAmount, setBankAmount] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  // Online platform amounts - object with platform_id as key
  const [platformAmounts, setPlatformAmounts] = useState({});

  // For expense mode
  const [expenseAmount, setExpenseAmount] = useState('');

  useEffect(() => {
    // Use Zustand for branches, fetch other data normally
    fetchBranches();
    Promise.all([
      api.get('/customers'), 
      api.get('/categories'),
      api.get('/platforms').catch(() => ({ data: [] })),
      api.get('/suppliers/names').catch(() => ({ data: [] })),
    ]).then(([cR, catR, pR, sR]) => {
      setCustomers(cR.data?.data || cR.data || []);
      setCategories(catR.data);
      setPlatforms(pR.data || []);
      setSuppliers(sR.data || []);
    }).catch(() => {});
    refreshStats();
  }, [fetchBranches]);

  // Set default branch when branches load or user has branch restriction
  useEffect(() => {
    if (branches.length > 0 && !branch) {
      const userBranch = user?.branch_id;
      setBranch(userBranch || branches[0].id);
    }
  }, [branches, branch, user]);

  const refreshStats = async () => {
    try {
      // Operators only see current day stats, admins/managers see all
      const isAdmin = user?.role === 'admin' || user?.role === 'manager';
      const today = format(new Date(), 'yyyy-MM-dd');
      const params = isAdmin ? '' : `?start_date=${today}&end_date=${today}`;
      const { data } = await api.get(`/dashboard/stats${params}`);
      setTodayStats({ 
        sales: data.total_sales || 0, 
        expenses: data.total_expenses || 0, 
        count: (data.total_sales_count || 0) + (data.total_expenses_count || 0),
        onlineSales: data.online_sales || 0
      });
    } catch {}
  };

  const totalRegularAmount = parseFloat(cashAmount || 0) + parseFloat(bankAmount || 0) + parseFloat(creditAmount || 0);
  const totalOnlineAmount = Object.values(platformAmounts).reduce((sum, amt) => sum + parseFloat(amt || 0), 0);

  const updatePlatformAmount = (platformId, amount) => {
    setPlatformAmounts(prev => ({ ...prev, [platformId]: amount }));
  };

  const getDateISO = () => {
    // Preserve selected date as-is (no UTC conversion) to avoid timezone date shift
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${selectedDate}T${hh}:${mm}:${ss}`;
  };

  const submitRegularSale = async () => {
    if (!branch) { toast.error('Select a branch'); return; }
    if (totalRegularAmount <= 0) { toast.error('Enter at least one payment amount'); return; }

    setSubmitting(true);
    try {
      const paymentDetails = [];
      const cash = parseFloat(cashAmount || 0);
      const bank = parseFloat(bankAmount || 0);
      const credit = parseFloat(creditAmount || 0);

      if (cash > 0) paymentDetails.push({ mode: 'cash', amount: cash, discount: 0 });
      if (bank > 0) paymentDetails.push({ mode: 'bank', amount: bank, discount: 0 });
      if (credit > 0) paymentDetails.push({ mode: 'credit', amount: credit, discount: 0 });

      const payload = {
        sale_type: 'pos',
        amount: totalRegularAmount,
        branch_id: branch,
        notes: description || 'POS Sale',
        date: getDateISO(),
        payment_details: paymentDetails,
      };
      if (credit > 0 && customerId) payload.customer_id = customerId;
      await api.post('/sales', payload);

      const modes = paymentDetails.map(p => p.mode).join(', ');
      toast.success(`Sale ${cur()} ${totalRegularAmount.toLocaleString()} recorded (${modes})`);
      setLastEntries([{ type: 'Sale', amount: totalRegularAmount, mode: modes }]);

      setCashAmount(''); setBankAmount(''); setCreditAmount('');
      setDescription(''); setCustomerId('');
      refreshStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const submitOnlineSales = async () => {
    if (!branch) { toast.error('Select a branch'); return; }
    
    // Get platforms with amounts > 0
    const salesData = platforms
      .filter(p => parseFloat(platformAmounts[p.id] || 0) > 0)
      .map(p => ({
        platform: p,
        amount: parseFloat(platformAmounts[p.id])
      }));

    if (salesData.length === 0) {
      toast.error('Enter amount for at least one platform');
      return;
    }

    setSubmitting(true);
    const results = [];

    try {
      for (const sale of salesData) {
        try {
          await api.post('/sales', {
            sale_type: 'online',
            amount: sale.amount,
            branch_id: branch,
            notes: `${sale.platform.name} Sale`,
            date: getDateISO(),
            payment_mode: 'online_platform',
            payment_details: [{ mode: 'online_platform', amount: sale.amount, discount: 0 }],
            platform_id: sale.platform.id,
            platform_status: 'pending',
          });
          results.push({ platform: sale.platform.name, amount: sale.amount, success: true });
        } catch (err) {
          results.push({ platform: sale.platform.name, amount: sale.amount, success: false, error: err.response?.data?.detail });
        }
      }

      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        const total = successful.reduce((s, r) => s + r.amount, 0);
        toast.success(`${successful.length} online sales recorded (${cur()} ${total.toLocaleString()})`);
      }
      if (failed.length > 0) {
        toast.error(`${failed.length} sales failed`);
      }

      setLastEntries(results.map(r => ({
        type: 'Online Sale',
        platform: r.platform,
        amount: r.amount,
        success: r.success
      })));

      // Only clear amounts for successful platforms, keep failed ones
      if (failed.length > 0) {
        const failedPlatformNames = new Set(failed.map(r => r.platform));
        const remaining = {};
        platforms.forEach(p => {
          if (failedPlatformNames.has(p.name) && parseFloat(platformAmounts[p.id] || 0) > 0) {
            remaining[p.id] = platformAmounts[p.id];
          }
        });
        setPlatformAmounts(remaining);
      } else {
        setPlatformAmounts({});
      }
      refreshStats();
    } catch (err) {
      toast.error('Failed to record sales');
    } finally {
      setSubmitting(false);
    }
  };

  const submitExpense = async () => {
    if (!branch) { toast.error('Select a branch'); return; }
    const amt = parseFloat(expenseAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }

    setSubmitting(true);
    try {
      await api.post('/expenses', {
        amount: amt, 
        category: category || 'General',
        branch_id: branch, 
        description: description || 'POS Expense',
        date: getDateISO(),
        payment_mode: paymentMode,
        supplier_id: supplierId || undefined,
      });
      const supplierName = suppliers.find(s => s.id === supplierId)?.name;
      toast.success(`Expense ${cur()} ${amt.toLocaleString()} recorded${supplierName ? ` - ${supplierName}` : ''}`);
      setLastEntries([{ type: 'Expense', amount: amt, mode: `${category || 'General'} (${paymentMode})`, supplier: supplierName }]);
      setExpenseAmount(''); setDescription(''); setSupplierId(''); setPaymentMode('cash');
      refreshStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  // Multi Supplier Payment Functions
  const addPaymentRow = () => {
    setSupplierPayments([...supplierPayments, { supplier_id: '', amount: '', payment_mode: 'cash' }]);
  };

  const removePaymentRow = (index) => {
    if (supplierPayments.length > 1) {
      setSupplierPayments(supplierPayments.filter((_, i) => i !== index));
    }
  };

  const updatePaymentRow = (index, field, value) => {
    const updated = [...supplierPayments];
    updated[index][field] = value;
    setSupplierPayments(updated);
  };

  const submitMultiplePayments = async () => {
    const validPayments = supplierPayments.filter(p => p.supplier_id && parseFloat(p.amount) > 0);
    if (validPayments.length === 0) {
      toast.error('Please add at least one payment with supplier and amount');
      return;
    }

    setSubmittingPayments(true);
    let successCount = 0;
    const entries = [];

    for (const payment of validPayments) {
      try {
        await api.post(`/suppliers/${payment.supplier_id}/pay-credit`, {
          amount: parseFloat(payment.amount),
          payment_mode: payment.payment_mode,
          branch_id: branch || '',
          date: getDateISO(),
        });
        const supplierName = suppliers.find(s => s.id === payment.supplier_id)?.name;
        entries.push({ type: 'Supplier Payment', amount: parseFloat(payment.amount), mode: payment.payment_mode, supplier: supplierName });
        successCount++;
      } catch (err) {
        const supplierName = suppliers.find(s => s.id === payment.supplier_id)?.name;
        toast.error(`Failed to pay ${supplierName}: ${err.response?.data?.detail || 'Error'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} supplier payment(s) recorded successfully!`);
      setLastEntries(entries);
      setSupplierPayments([{ supplier_id: '', amount: '', payment_mode: 'cash' }]);
      // Refresh supplier list to get updated balances
      api.get('/suppliers/names').then(res => setSuppliers(res.data || [])).catch(() => {});
    }
    setSubmittingPayments(false);
  };

  const totalPaymentsAmount = supplierPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  // Multi Expense Functions
  const addExpenseRow = () => {
    setExpenses([...expenses, { category: '', amount: '', payment_mode: 'cash', supplier_id: '', description: '' }]);
  };

  const removeExpenseRow = (index) => {
    if (expenses.length > 1) {
      setExpenses(expenses.filter((_, i) => i !== index));
    }
  };

  const updateExpenseRow = (index, field, value) => {
    const updated = [...expenses];
    updated[index][field] = value;
    setExpenses(updated);
  };

  const submitMultipleExpenses = async () => {
    if (!branch) { toast.error('Select a branch'); return; }
    
    const validExpenses = expenses.filter(e => parseFloat(e.amount) > 0);
    const _ve = validExpenses.map((e) => vatError(e)).find(Boolean);
    if (_ve) { toast.error(_ve); return; }
    if (validExpenses.length === 0) {
      toast.error('Please add at least one expense with amount');
      return;
    }

    setSubmittingExpenses(true);
    let successCount = 0;
    const entries = [];
    const failedIndices = new Set();

    for (let i = 0; i < validExpenses.length; i++) {
      const expense = validExpenses[i];
      try {
        await api.post('/expenses', {
          amount: parseFloat(expense.amount),
          category: expense.category || 'General',
          branch_id: branch,
          description: expense.description || 'POS Expense',
          date: getDateISO(),
          payment_mode: expense.payment_mode,
          ...(expense.payment_mode === 'cash' && expenseDrawer ? { drawer_shift_id: expenseDrawer } : {}),
          supplier_id: expense.supplier_id || undefined,
          ...vatPayload(expense),
        });
        const supplierName = suppliers.find(s => s.id === expense.supplier_id)?.name;
        entries.push({ 
          type: 'Expense', 
          amount: parseFloat(expense.amount), 
          mode: `${expense.category || 'General'} (${expense.payment_mode})`,
          supplier: supplierName 
        });
        successCount++;
      } catch (err) {
        failedIndices.add(i);
        toast.error(`Failed to record expense: ${err.response?.data?.detail || 'Error'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} expense(s) recorded successfully!`);
      setLastEntries(entries);
      // Keep only failed expenses in the form
      const remaining = failedIndices.size > 0
        ? validExpenses.filter((_, i) => failedIndices.has(i))
        : [{ category: '', amount: '', payment_mode: 'cash', supplier_id: '', description: '' }];
      setExpenses(remaining);
      refreshStats();
    }
    setSubmittingExpenses(false);
  };

  const totalExpensesAmount = expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  // Multi Supplier Bills Functions
  const addBillRow = () => {
    setSupplierBills([...supplierBills, { supplier_id: '', amount: '', payment_mode: 'credit', description: '' }]);
  };

  const removeBillRow = (index) => {
    if (supplierBills.length > 1) {
      setSupplierBills(supplierBills.filter((_, i) => i !== index));
    }
  };

  const updateBillRow = (index, field, value) => {
    const updated = [...supplierBills];
    updated[index][field] = value;
    setSupplierBills(updated);
  };

  const submitMultipleBills = async () => {
    if (!branch) { toast.error('Select a branch'); return; }
    
    const validBills = supplierBills.filter(b => b.supplier_id && parseFloat(b.amount) > 0);
    const _vb = validBills.map((b) => vatError(b)).find(Boolean);
    if (_vb) { toast.error(_vb); return; }
    if (validBills.length === 0) {
      toast.error('Please add at least one bill with supplier and amount');
      return;
    }

    setSubmittingBills(true);
    let successCount = 0;
    const entries = [];

    for (const bill of validBills) {
      try {
        const supplierName = suppliers.find(s => s.id === bill.supplier_id)?.name;
        await api.post('/expenses', {
          amount: parseFloat(bill.amount),
          category: 'Supplier Purchase',
          description: bill.description || `Purchase from ${supplierName}`,
          payment_mode: bill.payment_mode,
          supplier_id: bill.supplier_id,
          branch_id: branch,
          date: getDateISO(),
          ...vatPayload(bill),
        });
        // iter395f — Removed dual-write to /supplier-payments. Previously
        // we POSTed a `supplier_payments` row in addition to the expense,
        // which polluted the "Settlements of old credit bills" report
        // because cash/bank purchases were being mis-classified as
        // credit-bill settlements. The expense alone fully captures the
        // cash outflow; supplier_payments should only be created via the
        // explicit Pay Credit dialog when actually settling a credit bill.
        entries.push({ 
          type: 'Purchase Bill', 
          amount: parseFloat(bill.amount), 
          mode: bill.payment_mode,
          supplier: supplierName 
        });
        successCount++;
      } catch (err) {
        const supplierName = suppliers.find(s => s.id === bill.supplier_id)?.name;
        toast.error(`Failed to add bill for ${supplierName}: ${err.response?.data?.detail || 'Error'}`);
      }
    }

    if (successCount > 0) {
      const creditBills = validBills.filter(b => b.payment_mode === 'credit').length;
      const cashBills = validBills.filter(b => b.payment_mode !== 'credit').length;
      let msg = `${successCount} bill(s) recorded!`;
      if (creditBills > 0) msg += ` ${creditBills} on credit (balance updated).`;
      if (cashBills > 0) msg += ` ${cashBills} paid.`;
      toast.success(msg);
      setLastEntries(entries);
      setSupplierBills([{ supplier_id: '', amount: '', payment_mode: 'credit', description: '' }]);
      refreshStats();
      // Refresh suppliers to see updated balances
      api.get('/suppliers/names').then(res => setSuppliers(res.data || [])).catch(() => {});
    }
    setSubmittingBills(false);
  };

  const totalBillsAmount = supplierBills.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto py-4 px-2 space-y-4" data-testid="pos-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold font-outfit">Quick Entry</h1>
          <p className="text-sm text-muted-foreground">Sales & Expenses</p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <PageTour storageKey="tour_pos" steps={[
              { target: '[data-testid="pos-sale-btn"]', title: 'Record Sale', content: 'Quickly add sales with cash/bank split. Switch between branch and online sales.' },
              { target: '[data-testid="pos-expense-btn"]', title: 'Record Expense', content: 'Log expenses with category selection and payment method.' },
              { target: '[data-testid="pos-branch"]', title: 'Branch Select', content: 'Choose which branch this entry belongs to.' },
            ]} />
            {/* Narrative help button — opens the 4-step "First Sale"
                walkthrough modal. Sits next to the element-spotlight tour
                so first-time users have a richer onboarding option. */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowFirstSaleTour(true)}
              className="h-7 px-2 rounded-lg text-xs text-muted-foreground hover:text-violet-600 hover:bg-violet-50"
              data-testid="pos-help-btn"
              title="How does the POS work? (4-step walkthrough)"
            >
              <HelpCircle size={13} className="mr-1" /> Help
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <Card className="border-0 shadow-sm bg-success/10">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-success">{isAdmin ? 'Total Sales' : "Today's Sales"}</p>
              <p className="text-lg font-bold font-outfit text-success" data-testid="pos-total-sales">{cur()} {todayStats.sales.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-purple-50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-purple-600 flex items-center justify-center gap-1">
                <Truck size={12} /> {isAdmin ? 'Online Sales' : "Today's Online"}
              </p>
              <p className="text-lg font-bold font-outfit text-purple-700" data-testid="pos-online-sales">{cur()} {todayStats.onlineSales.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-error/10">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-error">{isAdmin ? 'Expenses' : "Today's Expenses"}</p>
              <p className="text-lg font-bold font-outfit text-error" data-testid="pos-expenses">{cur()} {todayStats.expenses.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-info/10">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-info">{isAdmin ? 'Net Profit' : "Today's Net"}</p>
              <p className="text-lg font-bold font-outfit text-info" data-testid="pos-net-profit">{cur()} {(todayStats.sales - todayStats.expenses).toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Entry Type Toggle */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 rounded-xl overflow-hidden border p-1 bg-muted">
          <button onClick={() => setEntryType('sale')}
            className={`py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${entryType === 'sale' ? 'bg-success text-white shadow' : 'bg-transparent text-muted-foreground hover:bg-white'}`}
            data-testid="pos-sale-btn-2">
            <ShoppingCart size={16} /> {t('qe_sales')}
          </button>
          <button onClick={() => setEntryType('expense')}
            className={`py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${entryType === 'expense' ? 'bg-error text-white shadow' : 'bg-transparent text-muted-foreground hover:bg-white'}`}
            data-testid="pos-expense-btn-2">
            <Receipt size={16} /> {t('qe_expenses')}
          </button>
          <button onClick={() => setEntryType('supplier_bill')}
            className={`py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${entryType === 'supplier_bill' ? 'bg-warning text-white shadow' : 'bg-transparent text-muted-foreground hover:bg-white'}`}
            data-testid="pos-supplier-bill-btn">
            <Package size={16} /> {t('qe_add_bills')}
          </button>
          <button onClick={() => setEntryType('supplier_payment')}
            className={`py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${entryType === 'supplier_payment' ? 'bg-info text-white shadow' : 'bg-transparent text-muted-foreground hover:bg-white'}`}
            data-testid="pos-supplier-payment-btn">
            <DollarSign size={16} /> {t('qe_pay_credit')}
          </button>
        </div>

        {/* Branch Selection */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="pos-branch-2">
                <SelectValue placeholder={t('qe_select_branch')} />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {branch && (
              <div className="mt-1.5">
                <BranchTaxBadge branchId={branch} compact />
              </div>
            )}
          </div>
          
          {/* Date Selection */}
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-4 text-muted-foreground z-10" />
            <Input 
              type="date" 
              value={selectedDate} 
              onChange={e => setSelectedDate(e.target.value)}
              className="h-12 pl-9 rounded-xl"
              data-testid="pos-date"
            />
          </div>
        </div>

        {/* SALES MODE */}
        {entryType === 'sale' && (
          <div className="space-y-4">
            {/* Sale Type Tabs */}
            <Tabs value={saleMode} onValueChange={setSaleMode}>
              <TabsList className="w-full">
                <TabsTrigger value="regular" className="flex-1" data-testid="regular-sale-tab">
                  <Banknote size={16} className="mr-1" /> {t('qe_regular_sale')}
                </TabsTrigger>
                <TabsTrigger value="online" className="flex-1 data-[state=active]:bg-purple-500 data-[state=active]:text-white" data-testid="online-sale-tab">
                  <Truck size={16} className="mr-1" /> {t('qe_online_sales')}
                </TabsTrigger>
              </TabsList>

              {/* Regular Sale */}
              <TabsContent value="regular" className="space-y-4 mt-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 space-y-4">
                    <Label className="text-xs text-muted-foreground font-medium">{t('qe_payment_amounts')}</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-success flex items-center gap-1">
                          <Banknote size={12} /> Cash
                        </Label>
                        <Input type="number" inputMode="decimal" value={cashAmount}
                          onChange={e => setCashAmount(e.target.value)} placeholder="0"
                          className="h-11 text-center font-semibold bg-success/10 border-success/30" data-testid="pos-cash" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-info flex items-center gap-1">
                          <CreditCard size={12} /> Bank
                        </Label>
                        <Input type="number" inputMode="decimal" value={bankAmount}
                          onChange={e => setBankAmount(e.target.value)} placeholder="0"
                          className="h-11 text-center font-semibold bg-info/10 border-info/30" data-testid="pos-bank" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-warning flex items-center gap-1">
                          <Users size={12} /> Credit
                        </Label>
                        <Input type="number" inputMode="decimal" value={creditAmount}
                          onChange={e => setCreditAmount(e.target.value)} placeholder="0"
                          className="h-11 text-center font-semibold bg-warning/10 border-warning/30" data-testid="pos-credit" />
                      </div>
                    </div>

                    {/* Customer for Credit */}
                    {parseFloat(creditAmount || 0) > 0 && (
                      <SearchableSelect
                        items={customers}
                        value={customerId}
                        onChange={setCustomerId}
                        placeholder={t('qe_search_customer')}
                      />
                    )}

                    {/* Total */}
                    {totalRegularAmount > 0 && (
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm font-medium">Total</span>
                        <span className="text-2xl font-bold text-success">{cur()} {totalRegularAmount.toLocaleString()}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Input value={description} onChange={e => setDescription(e.target.value)}
                  placeholder={t('qe_desc_optional')} className="h-11 rounded-xl" />

                <Button onClick={submitRegularSale} disabled={submitting || totalRegularAmount <= 0}
                  className="w-full h-14 rounded-xl text-lg font-semibold bg-success hover:bg-success">
                  {submitting ? <Loader2 className="animate-spin mr-2" /> : <Plus size={20} className="mr-2" />}
                  {t('qe_record_sale')} - {cur()} {totalRegularAmount.toLocaleString()}
                </Button>
              </TabsContent>

              {/* Online Sales - Multiple Platforms */}
              <TabsContent value="online" className="space-y-4 mt-4">
                <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Truck size={18} className="text-purple-600" />
                      <Label className="text-sm font-medium text-purple-700">{t('qe_platform_amounts')}</Label>
                    </div>
                    
                    <div className="space-y-2">
                      {platforms.filter(p => p.is_active !== false).map(platform => (
                        <div key={platform.id} className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label className="text-xs text-muted-foreground mb-1 block">
                              {platform.name} 
                              <span className="text-purple-500 ml-1">({platform.commission_rate}%)</span>
                            </Label>
                            <Input
                              type="number"
                              inputMode="decimal"
                              value={platformAmounts[platform.id] || ''}
                              onChange={e => updatePlatformAmount(platform.id, e.target.value)}
                              placeholder="0.00"
                              className={`h-10 text-center font-semibold ${PLATFORM_COLORS[platform.name] || 'bg-purple-50 border-purple-200'}`}
                              data-testid={`platform-${platform.id}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {platforms.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No platforms configured. <a href="/platforms" className="text-purple-600 underline">Add platforms</a>
                      </p>
                    )}

                    {/* Total Online */}
                    {totalOnlineAmount > 0 && (
                      <div className="flex items-center justify-between pt-3 border-t border-purple-200">
                        <div>
                          <span className="text-sm font-medium text-purple-700">Total Online Sales</span>
                          <p className="text-xs text-purple-500">
                            {Object.entries(platformAmounts).filter(([_, amt]) => parseFloat(amt || 0) > 0).length} platforms
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-purple-600">{cur()} {totalOnlineAmount.toLocaleString()}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Button onClick={submitOnlineSales} disabled={submitting || totalOnlineAmount <= 0}
                  className="w-full h-14 rounded-xl text-lg font-semibold bg-purple-500 hover:bg-purple-600">
                  {submitting ? <Loader2 className="animate-spin mr-2" /> : <Truck size={20} className="mr-2" />}
                  Record {Object.values(platformAmounts).filter(a => parseFloat(a || 0) > 0).length} Online Sales
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* EXPENSE MODE - General Business Expenses */}
        {entryType === 'expense' && (
          <div className="space-y-3">
            {/* Help Box */}
            <div className="p-3 bg-error/10 rounded-xl border border-error/30 text-xs">
              <p className="font-medium text-error">General Business Expenses</p>
              <p className="text-error mt-1">
                Record daily expenses like rent, utilities, salaries, supplies etc.
                For <strong>supplier purchases</strong>, use the <strong>Add Bills</strong> tab instead.
              </p>
            </div>
            {expenses.some((e) => e.payment_mode === 'cash') && (
              <DrawerSourceSelect branchId={branch} value={expenseDrawer} onChange={setExpenseDrawer} />
            )}

            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-error">{t('qe_record_biz_expenses')}</Label>
              <Button 
                type="button" size="sm" variant="outline" onClick={addExpenseRow}
                className="h-8 text-xs border-error/30 text-error hover:bg-error/10" data-testid="add-expense-row">
                <Plus size={14} className="mr-1" /> Add More
              </Button>
            </div>

            {/* Expense Rows */}
            <div className="space-y-2">
              {expenses.map((expense, index) => (
                <div key={index} className="p-3 bg-error/10 rounded-xl border border-error/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-error">Expense #{index + 1}</span>
                    {expenses.length > 1 && (
                      <button onClick={() => removeExpenseRow(index)} className="text-xs text-error hover:text-error" data-testid={`remove-expense-${index}`}>Remove</button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" placeholder={t('qe_amount_sr')} value={expense.amount}
                      onChange={(e) => updateExpenseRow(index, 'amount', e.target.value)}
                      className="h-10 rounded-lg bg-white" data-testid={`expense-amount-${index}`} />
                    <Select value={expense.category || "general"} onValueChange={(v) => updateExpenseRow(index, 'category', v)}>
                      <SelectTrigger className="h-10 rounded-lg bg-white" data-testid={`expense-category-${index}`}>
                        <SelectValue placeholder={t('qe_expense_type')} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
                        <SelectItem value="General">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Select value={expense.payment_mode} onValueChange={(v) => updateExpenseRow(index, 'payment_mode', v)}>
                      <SelectTrigger className="h-10 rounded-lg bg-white" data-testid={`expense-mode-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash"><div className="flex items-center gap-2"><Banknote size={14} /> {t('qe_paid_cash')}</div></SelectItem>
                        <SelectItem value="bank"><div className="flex items-center gap-2"><CreditCard size={14} /> {t('qe_paid_bank')}</div></SelectItem>
                      </SelectContent>
                    </Select>
                    <SearchableSelect
                      items={[{id: '', name: 'No Supplier (General)'}, ...suppliers]}
                      value={expense.supplier_id || ''}
                      onChange={(v) => updateExpenseRow(index, 'supplier_id', v)}
                      placeholder={t('qe_search_supplier')}
                    />
                  </div>

                  <Input placeholder={t('qe_desc_invoice_optional')} value={expense.description}
                    onChange={(e) => updateExpenseRow(index, 'description', e.target.value)}
                    className="h-9 rounded-lg bg-white text-sm" data-testid={`expense-description-${index}`} />
                  <VatTreatmentFields compact value={expense} amount={expense.amount} testPrefix={`expense-vat-${index}`}
                    onChange={(p) => Object.entries(p).forEach(([k, v]) => updateExpenseRow(index, k, v))} />
                </div>
              ))}
            </div>

            {/* Total & Submit */}
            <div className="p-3 bg-error/20 rounded-xl border border-error/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-error">Total Expenses</span>
                <span className="text-lg font-bold text-error">{cur()} {totalExpensesAmount.toLocaleString()}</span>
              </div>
              <div className="text-xs text-error">
                {expenses.filter(e => parseFloat(e.amount) > 0).length} expense(s) to record
              </div>
            </div>

            <Button onClick={submitMultipleExpenses} disabled={submittingExpenses || totalExpensesAmount === 0}
              className="w-full h-14 rounded-xl text-lg font-semibold bg-error hover:bg-error" data-testid="submit-expenses">
              {submittingExpenses ? <Loader2 className="animate-spin mr-2" /> : <Receipt size={20} className="mr-2" />}
              {t('qe_record_expenses')} - {cur()} {totalExpensesAmount.toLocaleString()}
            </Button>
          </div>
        )}

        {/* SUPPLIER BILLS SECTION */}
        {entryType === 'supplier_bill' && (
          <div className="space-y-3">
            {/* iter933 — Quick bill capture via the existing QR scan page. */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl border-warning/40 text-warning hover:bg-warning/10"
              onClick={() => navigate('/scan-invoice')}
              data-testid="quick-entry-scan-qr-btn"
            >
              <ScanLine size={16} className="mr-2" />{t('qe_scan_qr')}
            </Button>
            {/* Help Box */}
            <div className="p-3 bg-warning/10 rounded-xl border border-warning/30 text-xs">
              <p className="font-medium text-warning">📦 Purchase Bills from Suppliers</p>
              <p className="text-warning mt-1">
                <strong>On Credit</strong> = Pay supplier later (balance increases) | 
                <strong> Paid</strong> = Already paid (no balance)
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-warning">{t('qe_supplier_bills')}</Label>
              <Button 
                type="button" 
                size="sm" 
                variant="outline"
                onClick={addBillRow}
                className="h-8 text-xs border-warning/30 text-warning hover:bg-warning/10"
                data-testid="add-bill-row"
              >
                <Plus size={14} className="mr-1" /> Add More
              </Button>
            </div>

            {/* Bill Rows */}
            <div className="space-y-2">
              {supplierBills.map((bill, index) => (
                <div key={index} className="p-3 bg-warning/10 rounded-xl border border-warning/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-warning">Bill #{index + 1}</span>
                    {supplierBills.length > 1 && (
                      <button 
                        onClick={() => removeBillRow(index)}
                        className="text-xs text-error hover:text-error"
                        data-testid={`remove-bill-${index}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Supplier Selection */}
                  <SearchableSelect
                    items={suppliers}
                    value={bill.supplier_id || ''}
                    onChange={(v) => updateBillRow(index, 'supplier_id', v)}
                    placeholder={t('qe_search_supplier')}
                  />

                  <div className="grid grid-cols-2 gap-2">
                    {/* Amount */}
                    <Input
                      type="number"
                      placeholder={t('qe_amount')}
                      value={bill.amount}
                      onChange={(e) => updateBillRow(index, 'amount', e.target.value)}
                      className="h-10 rounded-lg bg-white"
                      data-testid={`bill-amount-${index}`}
                    />
                    
                    {/* Payment Mode - Credit, Cash, or Bank */}
                    <Select 
                      value={bill.payment_mode} 
                      onValueChange={(v) => updateBillRow(index, 'payment_mode', v)}
                    >
                      <SelectTrigger className="h-10 rounded-lg bg-white" data-testid={`bill-mode-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">
                          <div className="flex items-center gap-2">
                            <Receipt size={14} className="text-warning" /> On Credit
                          </div>
                        </SelectItem>
                        <SelectItem value="cash">
                          <div className="flex items-center gap-2">
                            <Banknote size={14} className="text-success" /> Cash
                          </div>
                        </SelectItem>
                        <SelectItem value="bank">
                          <div className="flex items-center gap-2">
                            <CreditCard size={14} className="text-info" /> Bank
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <Input
                    placeholder={t('qe_invoice_or_desc')}
                    value={bill.description}
                    onChange={(e) => updateBillRow(index, 'description', e.target.value)}
                    className="h-9 rounded-lg bg-white text-sm"
                    data-testid={`bill-description-${index}`}
                  />
                  <VatTreatmentFields compact value={bill} amount={bill.amount} testPrefix={`bill-vat-${index}`}
                    onChange={(p) => Object.entries(p).forEach(([k, v]) => updateBillRow(index, k, v))} />

                  {/* Credit indicator */}
                  {bill.payment_mode === 'credit' && bill.supplier_id && (
                    <div className="text-[10px] text-warning bg-warning/20 px-2 py-1 rounded">
                      Will add to balance (pay later)
                    </div>
                  )}
                  {bill.payment_mode !== 'credit' && bill.supplier_id && (
                    <div className="text-[10px] text-success bg-success/20 px-2 py-1 rounded">
                      Already paid - no balance change
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Total & Submit */}
            <div className="p-3 bg-warning/20 rounded-xl border border-warning/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-warning">Total Bills</span>
                <span className="text-lg font-bold text-warning">{cur()} {totalBillsAmount.toLocaleString()}</span>
              </div>
              <div className="text-xs text-warning">
                {supplierBills.filter(b => b.supplier_id && parseFloat(b.amount) > 0).length} bill(s) • 
                {supplierBills.filter(b => b.payment_mode === 'credit' && parseFloat(b.amount) > 0).length} on credit
              </div>
            </div>

            <Button 
              onClick={submitMultipleBills} 
              disabled={submittingBills || totalBillsAmount === 0}
              className="w-full h-14 rounded-xl text-lg font-semibold bg-warning hover:bg-warning"
              data-testid="submit-bills"
            >
              {submittingBills ? <Loader2 className="animate-spin mr-2" /> : <Package size={20} className="mr-2" />}
              {t('qe_add_bills')} - {cur()} {totalBillsAmount.toLocaleString()}
            </Button>
          </div>
        )}

        {/* SUPPLIER PAYMENTS SECTION */}
        {entryType === 'supplier_payment' && (
          <div className="space-y-3">
            {/* Help Box */}
            <div className="p-3 bg-info/10 rounded-xl border border-info/30 text-xs">
              <p className="font-medium text-info">💰 Pay Credit Back</p>
              <p className="text-info mt-1">
                Use this to <strong>PAY BACK</strong> credit you owe to suppliers. 
                This reduces your credit balance with them.
              </p>
              <p className="text-info mt-1">
                For <strong>Purchase Bills</strong> use the <strong>Add Bills</strong> tab.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-info">{t('qe_pay_credit_suppliers')}</Label>
              <Button 
                type="button" 
                size="sm" 
                variant="outline"
                onClick={addPaymentRow}
                className="h-8 text-xs border-info/30 text-info hover:bg-info/10"
                data-testid="add-payment-row"
              >
                <Plus size={14} className="mr-1" /> Add More
              </Button>
            </div>

            {/* Payment Rows */}
            <div className="space-y-2">
              {supplierPayments.map((payment, index) => (
                <div key={index} className="p-3 bg-info/10 rounded-xl border border-info/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-info">Payment #{index + 1}</span>
                    {supplierPayments.length > 1 && (
                      <button 
                        onClick={() => removePaymentRow(index)}
                        className="text-xs text-error hover:text-error"
                        data-testid={`remove-payment-${index}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Supplier Selection */}
                  <SearchableSelect
                    items={suppliers}
                    value={payment.supplier_id || ''}
                    onChange={(v) => updatePaymentRow(index, 'supplier_id', v)}
                    placeholder={t('qe_search_supplier')}
                  />

                  <div className="grid grid-cols-2 gap-2">
                    {/* Amount */}
                    <Input
                      type="number"
                      placeholder={t('qe_amount')}
                      value={payment.amount}
                      onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)}
                      className="h-10 rounded-lg bg-white"
                      data-testid={`payment-amount-${index}`}
                    />
                    
                    {/* Payment Mode */}
                    <Select 
                      value={payment.payment_mode} 
                      onValueChange={(v) => updatePaymentRow(index, 'payment_mode', v)}
                    >
                      <SelectTrigger className="h-10 rounded-lg bg-white" data-testid={`payment-mode-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">
                          <div className="flex items-center gap-2">
                            <Banknote size={14} /> Cash
                          </div>
                        </SelectItem>
                        <SelectItem value="bank">
                          <div className="flex items-center gap-2">
                            <CreditCard size={14} /> Bank
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            {/* Total & Submit */}
            <div className="p-3 bg-info/20 rounded-xl border border-info/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-info">Total Payments</span>
                <span className="text-lg font-bold text-info">{cur()} {totalPaymentsAmount.toLocaleString()}</span>
              </div>
              <div className="text-xs text-info mb-2">
                {supplierPayments.filter(p => p.supplier_id && parseFloat(p.amount) > 0).length} valid payment(s) - will reduce credit balance
              </div>
            </div>

            <Button 
              onClick={submitMultiplePayments} 
              disabled={submittingPayments || totalPaymentsAmount === 0}
              className="w-full h-14 rounded-xl text-lg font-semibold bg-info hover:bg-info"
              data-testid="submit-supplier-payments"
            >
              {submittingPayments ? <Loader2 className="animate-spin mr-2" /> : <DollarSign size={20} className="mr-2" />}
              Pay Credit - {cur()} {totalPaymentsAmount.toLocaleString()}
            </Button>
          </div>
        )}

        {/* Last Entries */}
        {lastEntries.length > 0 && (
          <Card className="border-0 shadow-sm bg-muted/50">
            <CardContent className="p-3">
              <Label className="text-xs text-muted-foreground mb-2 block">{t('qe_last_recorded')}</Label>
              <div className="space-y-1">
                {lastEntries.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {entry.success === false ? (
                        <Badge variant="destructive" className="text-xs">Failed</Badge>
                      ) : (
                        <CheckCircle size={14} className="text-success" />
                      )}
                      <span>{entry.platform || entry.supplier || entry.type}</span>
                      {entry.mode === 'credit' && (
                        <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">On Credit</Badge>
                      )}
                      {entry.mode === 'cash' && (
                        <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">Cash Paid</Badge>
                      )}
                      {entry.mode === 'bank' && (
                        <Badge variant="outline" className="text-[10px] bg-info/10 text-info border-info/30">Bank Paid</Badge>
                      )}
                    </div>
                    <span className="font-semibold">{cur()} {entry.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      {/* 4-step First Sale walkthrough — opened by the header Help button. */}
      <PosFirstSaleTour open={showFirstSaleTour} onOpenChange={setShowFirstSaleTour} />
    </DashboardLayout>
  );
}
